import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, ArrowRight, Trophy, TrendUp, Pause, Prohibit, TrendDown, Clock } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { api, generateMarketDecisionChallenge, getActiveWeeklyChallenge, getChallengeLeaderboard, getContext } from "../lib/api";
import { SeasonEndBanner, isBetweenSeasons } from "../components/SeasonEndBanner";

export default function TransferDeskHub() {
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [weekly, setWeekly] = useState(null);
    const [weeklyLeaderboard, setWeeklyLeaderboard] = useState(null);
    const [context, setContext] = useState(null);

    const reload = () => {
        setLoading(true);
        api.get("/challenges", { params: { challenge_type: "MARKET_DECISION", status: "published", limit: 30 } })
            .then((r) => setChallenges(r.data || []))
            .catch(() => setChallenges([]))
            .finally(() => setLoading(false));
        getActiveWeeklyChallenge()
            .then((d) => setWeekly(d))
            .catch(() => setWeekly({ active: false, challenge: null }));
    };
    useEffect(reload, []);

    useEffect(() => {
        getContext(3).then(setContext).catch(() => setContext(null));
    }, []);
    const between = isBetweenSeasons(context);

    useEffect(() => {
        if (!weekly?.challenge?.id) return;
        getChallengeLeaderboard(weekly.challenge.id, 5)
            .then((d) => setWeeklyLeaderboard(d))
            .catch(() => setWeeklyLeaderboard(null));
    }, [weekly?.challenge?.id]);

    const generateNew = async () => {
        setGenerating(true);
        try {
            const ch = await generateMarketDecisionChallenge({
                mode: "MARKET_CALL",
                count: 10,
                segments: ["market", "hidden_gems", "traps"],
                duration_window: 3,
            });
            toast.success(`New Transfer Desk ready · GW${ch.duration?.fromGameweek}–${ch.duration?.toGameweek}`);
            window.location.assign(`/play/market/${ch.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed.");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <AppShell>
            <div className="space-y-8" data-testid="transfer-desk-hub">
                {/* Active weekly hero */}
                {weekly?.active && weekly?.challenge ? (
                    <WeeklyHero challenge={weekly.challenge} activeUntil={weekly.active_until} leaderboard={weeklyLeaderboard} />
                ) : (
                    <section className="border border-fa-violet/30 bg-[#0d0d0d] p-6 md:p-10 relative overflow-hidden" data-testid="hub-static-hero">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                            <Lightning size={12} weight="fill" />
                            Market Decision Game
                        </div>
                        <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] mt-3">
                            Transfer
                            <br />
                            <span className="text-fa-violet">Desk.</span>
                        </h1>
                        <p className="text-white/70 max-w-2xl leading-relaxed mt-4">
                            Judge 10 of the most-talked-about FPL players each gameweek as
                            <strong className="text-fa-mint"> BUY</strong>,
                            <strong className="text-fa-sky"> KEEP</strong>,
                            <strong className="text-[#F5A78D]"> AVOID</strong> or
                            <strong className="text-[#FF6B6B]"> SELL</strong>.
                            Graded automatically when the GW resolves.
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-6">
                            <Button
                                onClick={generateNew}
                                disabled={generating || between}
                                className="rounded-none bg-fa-violet hover:bg-fa-violetHover text-white uppercase font-black font-display tracking-wide px-6 py-5 disabled:opacity-40"
                                data-testid="hub-generate-btn"
                            >
                                {between ? "Sezóna neaktivní" : generating ? "Generating…" : "Generate fresh challenge →"}
                            </Button>
                            <Link to="/leaderboard" className="font-mono text-[11px] uppercase tracking-widest text-fa-cream hover:text-white flex items-center gap-1.5">
                                <Trophy size={12} weight="fill" />
                                See top advisors
                            </Link>
                        </div>
                        <p className="font-mono text-[10px] text-white/40 mt-3">
                            Auto-generated weekly challenges arrive every Tuesday · stay tuned.
                        </p>
                    </section>
                )}

                {/* List */}
                <section>
                    <SeasonEndBanner context={context} className="mb-4" />
                    <div className="flex items-end justify-between mb-4">
                        <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">
                            All challenges
                        </h2>
                        <div className="flex-1 h-px bg-white/10 ml-4" />
                    </div>
                    {between ? (
                        <div className="border border-white/10 bg-[#0d0d0d] p-10 text-center" data-testid="hub-between-seasons">
                            <p className="font-mono text-sm text-white/60">
                                Transfer výzvy potřebují živou sezónu — vrátí se po startu 2026-27.
                            </p>
                        </div>
                    ) : loading ? (
                        <div className="font-mono text-sm text-white/40 text-center py-12" data-testid="hub-loading">
                            Loading challenges…
                        </div>
                    ) : challenges.length === 0 ? (
                        <div className="border border-white/10 bg-[#0d0d0d] p-10 text-center" data-testid="hub-empty">
                            <p className="font-mono text-sm text-white/60">
                                No Transfer Desk challenges live right now.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hub-grid">
                            {challenges.map((c) => (
                                <ChallengeRow key={c.id} challenge={c} highlight={weekly?.challenge?.id === c.id} />
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </AppShell>
    );
}

function WeeklyHero({ challenge, activeUntil, leaderboard }) {
    const dur = challenge.duration || {};
    const playerCount = (challenge.players || []).length;
    const segs = (challenge.segmentsUsed || []).join(" · ");
    const auDate = activeUntil ? new Date(activeUntil) : null;
    const msLeft = auDate ? auDate.getTime() - Date.now() : 0;
    const hoursLeft = Math.max(0, Math.floor(msLeft / 3_600_000));
    const daysLeft = Math.floor(hoursLeft / 24);
    const remaining = daysLeft > 0
        ? `${daysLeft}d ${hoursLeft % 24}h until deadline`
        : `${hoursLeft}h until deadline`;
    return (
        <section className="border-2 border-fa-violet bg-gradient-to-br from-fa-violet/20 via-[#0d0d0d] to-[#0d0d0d] p-6 md:p-10 relative overflow-hidden" data-testid="weekly-hero">
            <div className="grain-overlay" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 space-y-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2 animate-pulse">
                        <span className="inline-block w-1.5 h-1.5 bg-fa-yellow rounded-full" />
                        Live this week
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                        {challenge.title}
                    </h1>
                    <p className="font-mono text-sm text-white/70 leading-relaxed max-w-xl">
                        {challenge.shortDescription}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-white/50">
                        <span className="text-fa-cream">GW {dur.fromGameweek}–{dur.toGameweek}</span>
                        <span>· {playerCount} players</span>
                        {segs && <span>· {segs}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        <Link
                            to={`/play/market/${challenge.id}`}
                            className="bg-fa-yellow text-black font-display font-black uppercase tracking-wide px-6 py-3 hover:bg-fa-yellow/90 transition-colors flex items-center gap-2"
                            data-testid="weekly-play-btn"
                        >
                            Play this week's challenge
                            <ArrowRight size={14} weight="bold" />
                        </Link>
                        <span className="font-mono text-[11px] text-fa-mint flex items-center gap-1.5" data-testid="weekly-deadline">
                            <Clock size={11} weight="fill" />
                            {remaining}
                        </span>
                    </div>
                </div>
                <div className="lg:col-span-5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow flex items-center gap-1.5 mb-3">
                        <Trophy size={11} weight="fill" />
                        Live leaderboard
                    </div>
                    <div className="border border-white/10 bg-black/40 p-4 space-y-1" data-testid="weekly-leaderboard">
                        {!leaderboard || leaderboard.items.length === 0 ? (
                            <div className="font-mono text-xs text-white/40 py-3 text-center">
                                Be the first to submit your picks.
                            </div>
                        ) : (
                            leaderboard.items.slice(0, 5).map((it, idx) => (
                                <div key={`${it.user_id}-${idx}`} className="flex items-center justify-between gap-2 py-1" data-testid={`weekly-lb-row-${idx}`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="font-mono text-[11px] text-white/40 tabular-nums">
                                            {it.rank ? `#${it.rank}` : "—"}
                                        </span>
                                        <span className="font-mono text-sm text-white truncate">
                                            {it.name}
                                            {it.is_guest && <span className="ml-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40">guest</span>}
                                        </span>
                                    </div>
                                    <span className="font-mono text-[11px] tabular-nums">
                                        {it.status === "resolved"
                                            ? <span className="text-fa-mint">+{it.total_score}</span>
                                            : <span className="text-white/40">submitted</span>}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function ChallengeRow({ challenge, highlight }) {
    const dur = challenge.duration || {};
    const playerCount = (challenge.players || []).length;
    const segs = (challenge.segmentsUsed || []).join(" · ");
    const created = challenge.createdAt ? new Date(challenge.createdAt) : null;
    return (
        <Link
            to={`/play/market/${challenge.id}`}
            className={`block border bg-[#0d0d0d] p-4 transition-colors group ${highlight ? "border-fa-violet" : "border-white/10 hover:border-fa-violet/60"}`}
            data-testid={`hub-row-${challenge.id}`}
        >
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                <Lightning size={10} weight="fill" />
                {challenge.mode || "MARKET_CALL"}
                {highlight && (
                    <span className="ml-auto text-[9px] tracking-widest text-fa-yellow bg-fa-yellow/10 border border-fa-yellow/40 px-1.5 py-0.5">
                        THIS WEEK
                    </span>
                )}
                <span className={highlight ? "" : "ml-auto text-white/30"}>·</span>
                <span className="text-fa-cream">GW {dur.fromGameweek}–{dur.toGameweek}</span>
            </div>
            <h3 className="font-display font-black text-lg uppercase tracking-tight mt-2 group-hover:text-fa-violet">
                {challenge.title}
            </h3>
            <p className="font-mono text-[11px] text-white/60 line-clamp-2 mt-2 leading-snug">
                {challenge.shortDescription}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 inline-flex items-center gap-1">
                    <TrendUp size={10} weight="bold" /> {playerCount} players
                </span>
                {segs && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        · {segs}
                    </span>
                )}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                <span className="font-mono text-[10px] text-white/30">
                    {created ? created.toLocaleDateString() : ""}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-fa-violet flex items-center gap-1">
                    Play <ArrowRight size={10} weight="bold" />
                </span>
            </div>
        </Link>
    );
}

// Re-export icons used in JSX (for tree-shaking awareness only — not strictly needed)
void Pause; void Prohibit; void TrendDown;
